import { Box, Chip, Stack, Tooltip } from '@mui/material'
import React, { useLayoutEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import ParentCellOrFragment from './ParentCellOrFragment'

type TagsCellProps = {
  tags: Array<string>
  amountOfTagsShow: number
  disableCell?: boolean
  // Fit as many whole chips as the cell width allows, then a compact "+N" counter, revealing the full
  // list on hover. Use in dense tables where the default click-to-expand-inline would be clipped.
  singleLine?: boolean
}

const CHIP_GAP_PX = 8

// Measures the natural chip widths in a hidden row and returns how many leading chips fit the given
// width, always reserving room for the "+N" counter when some chips are left over (so it never clips).
const computeVisibleCount = (availableWidth: number, chipWidths: number[], counterWidth: number): number => {
  let used = 0
  let count = 0
  for (let index = 0; index < chipWidths.length; index++) {
    const withChip = used + (count > 0 ? CHIP_GAP_PX : 0) + chipWidths[index]
    const moreFollow = index < chipWidths.length - 1
    const reserve = moreFollow ? CHIP_GAP_PX + counterWidth : 0
    if (withChip + reserve > availableWidth) break
    used = withChip
    count++
  }
  return count
}

const FittedTags: React.FC<{ tags: string[] }> = ({ tags }) => {
  const { formatMessage } = useIntl()
  const containerRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)
  const [visibleCount, setVisibleCount] = useState(tags.length)

  useLayoutEffect(() => {
    const container = containerRef.current
    const measure = measureRef.current
    if (!container || !measure) return

    const recompute = (): void => {
      const chipWidths = Array.from(measure.querySelectorAll<HTMLElement>('[data-tag-chip]')).map(el => el.offsetWidth)
      const counterWidth = measure.querySelector<HTMLElement>('[data-tag-counter]')?.offsetWidth ?? 0
      setVisibleCount(computeVisibleCount(container.clientWidth, chipWidths, counterWidth))
    }

    recompute()
    const resizeObserver = new ResizeObserver(recompute)
    resizeObserver.observe(container)
    return () => resizeObserver.disconnect()
  }, [tags])

  const overflowCount = tags.length - visibleCount

  return (
    <Tooltip title={tags.join(', ')} disableHoverListener={!tags.length}>
      <Stack
        ref={containerRef}
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ flexWrap: 'nowrap', overflow: 'hidden', minWidth: 0, position: 'relative', width: '100%' }}
      >
        {tags.slice(0, visibleCount).map(tag => (
          <Chip key={tag} variant="outlined" size="small" label={tag} />
        ))}
        {overflowCount > 0 && (
          <Chip
            variant="filled"
            size="small"
            label={formatMessage({ id: 'general.plusCount' }, { count: overflowCount })}
            sx={{ flexShrink: 0 }}
          />
        )}
        {/* Hidden row rendering every chip at natural width so we can measure how many fit. */}
        <Box
          ref={measureRef}
          aria-hidden
          sx={{ position: 'absolute', visibility: 'hidden', display: 'flex', pointerEvents: 'none' }}
        >
          {tags.map(tag => (
            <Chip key={tag} data-tag-chip variant="outlined" size="small" label={tag} />
          ))}
          {/* Measure the counter with the same localized label at the max count, so the reserved width
              matches the widest counter that could actually render (avoids clipping in other locales). */}
          <Chip
            data-tag-counter
            variant="filled"
            size="small"
            label={formatMessage({ id: 'general.plusCount' }, { count: tags.length })}
          />
        </Box>
      </Stack>
    </Tooltip>
  )
}

const TagsCell: React.FC<TagsCellProps> = ({ tags, amountOfTagsShow, disableCell, singleLine }) => {
  const { formatMessage } = useIntl()
  const [showAll, setShowAll] = useState(false)

  if (singleLine) {
    // Fill the cell so FittedTags measures the full column width. Without this the wrapper shrinks to
    // its content, and each chip it hides shrinks the wrapper further, collapsing everything to "+N".
    return (
      <ParentCellOrFragment disableCell={disableCell} sx={{ width: '100%' }}>
        <FittedTags tags={tags} />
      </ParentCellOrFragment>
    )
  }

  if (showAll) {
    return (
      <ParentCellOrFragment disableCell={disableCell}>
        {tags.map(tag => (
          <Chip key={tag} variant="outlined" size="small" label={tag} sx={{ mr: 1, my: 0.5 }} />
        ))}
      </ParentCellOrFragment>
    )
  }

  return (
    <ParentCellOrFragment disableCell={disableCell}>
      {tags?.slice(0, amountOfTagsShow).map(tag => (
        <Chip key={tag} variant="outlined" size="small" label={tag} sx={{ mr: 1, my: 0.5 }} />
      ))}

      {tags?.length > amountOfTagsShow && (
        <Tooltip title={formatMessage({ id: 'general.showAll' })}>
          <Chip
            variant="filled"
            size="small"
            label={formatMessage({ id: 'schemas.supplier.providerTags' }, { amount: tags?.length - amountOfTagsShow })}
            sx={{ mr: 1, my: 0.5 }}
            onClick={() => setShowAll(true)}
          />
        </Tooltip>
      )}
    </ParentCellOrFragment>
  )
}

export default TagsCell
